/**
 * SWOTScreen.jsx - SWOT Analysis Screen
 *
 * Route: /swot?kid={instanceId}
 *
 * Fetches workshop clusters with sentiment analysis and displays
 * them in a SWOT matrix (Strengths, Weaknesses, Opportunities, Threats).
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { RefreshCw, Loader2 } from 'lucide-react';
import SWOTView, { ViewModeToggle } from '../components/SWOTAnalysis/SWOTView';
import MainViewHeader from '../components/MainViewHeader';
import API_ENDPOINTS from '../config/api';
import SocketService from '../../../../packages/shared/socket';
import { useInstance } from '../contexts/InstanceContext';
import { useTranslation } from '../hooks/useTranslation.jsx';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: #0a0a0a;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MainContent = styled.main`
  flex: 1;
  overflow: hidden;
  margin-top: 60px; /* Account for fixed header */
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 8px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  &.spinning svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);

  .stat {
    display: flex;
    align-items: center;
    gap: 4px;

    span {
      color: white;
      font-weight: 500;
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;
  color: white;

  .spinner {
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #1a1a2e;
  border: 1px solid #F44336;
  border-radius: 8px;
  padding: 24px 32px;
  color: white;
  text-align: center;

  h3 {
    margin: 0 0 12px 0;
    color: #F44336;
  }

  p {
    margin: 0 0 16px 0;
    color: #aaa;
  }

  button {
    background: #F44336;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    color: white;
    cursor: pointer;

    &:hover {
      background: #d32f2f;
    }
  }
`;

function SWOTScreen({
  isEmbedded = false,
  screenMode = false
}) {
  const { currentInstance } = useInstance();
  const { t } = useTranslation();
  const [swotData, setSwotData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [instanceId, setInstanceId] = useState(null);
  const [instanceTitle, setInstanceTitle] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  // Get instance ID from URL params or context
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const kid = params.get('kid') || params.get('instanceId') || currentInstance?.id;
    if (kid) {
      setInstanceId(kid);
    }
  }, [currentInstance]);

  // Fetch instance title
  useEffect(() => {
    if (!instanceId) return;

    const fetchInstanceTitle = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.INSTANCES());
        if (response.ok) {
          const instances = await response.json();
          const instance = instances.find(i => i.id === instanceId);
          if (instance) {
            setInstanceTitle(instance.title || '');
          }
        }
      } catch (err) {
        console.error('Failed to fetch instance title:', err);
      }
    };

    fetchInstanceTitle();
  }, [instanceId]);

  // Connect to socket for green connection indicator
  // Skip if in screenMode - ScreenClient manages the socket
  useEffect(() => {
    if (isEmbedded || screenMode) return;

    const socketUrl = API_ENDPOINTS.SOCKET_URL();
    SocketService.connect(socketUrl, {
      onConnect: () => {
        console.log('[SWOT] Connected to socket');
      },
      onDisconnect: () => {
        console.log('[SWOT] Disconnected from socket');
      }
    });

    return () => {
      // Don't disconnect on unmount - let App.jsx handle the connection
    };
  }, [isEmbedded, screenMode]);

  // Fetch SWOT analysis
  const fetchSWOTAnalysis = useCallback(async (forceRefresh = false) => {
    if (!instanceId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.RAG_ANALYZE_SWOT(instanceId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          threshold: 0.6,
          minMembers: 2,
          maxClusters: 20,
          forceRefresh
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[SWOT] Analysis received:', data.stats);
      setSwotData(data);
    } catch (err) {
      console.error('Failed to fetch SWOT analysis:', err);
      // Handle network errors and circular reference issues gracefully
      setError({ message: err?.message || null });
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    if (instanceId) {
      fetchSWOTAnalysis();
    }
  }, [instanceId, fetchSWOTAnalysis]);

  return (
    <Container>
      <MainViewHeader
        activeView="swot"
        instanceTitle={instanceTitle}
      >
        <Controls>
          <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />

          {swotData?.stats && (
            <StatsBar>
              <div className="stat">
                S: <span>{swotData.stats.quadrantCounts?.strength || 0}</span>
              </div>
              <div className="stat">
                W: <span>{swotData.stats.quadrantCounts?.weakness || 0}</span>
              </div>
              <div className="stat">
                O: <span>{swotData.stats.quadrantCounts?.opportunity || 0}</span>
              </div>
              <div className="stat">
                T: <span>{swotData.stats.quadrantCounts?.threat || 0}</span>
              </div>
            </StatsBar>
          )}

          <IconButton
            onClick={() => fetchSWOTAnalysis(true)}
            disabled={loading}
            className={loading ? 'spinning' : ''}
            title={t('swotView.refresh')}
          >
            <RefreshCw />
          </IconButton>
        </Controls>
      </MainViewHeader>

      <MainContent>
        {loading && !swotData && (
          <LoadingOverlay>
            <Loader2 className="spinner" size={40} />
            <div>{t('swotView.analysing')}</div>
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '8px' }}>
              {t('swotView.mayTakeAWhile')}
            </div>
          </LoadingOverlay>
        )}

        {error && !swotData && (
          <ErrorMessage>
            <h3>{t('common.error')}</h3>
            <p>{error.message || t('swotView.networkError')}</p>
            <button onClick={fetchSWOTAnalysis}>{t('swotView.retry')}</button>
          </ErrorMessage>
        )}

        {swotData && (
          <SWOTView
            swotItems={swotData.swotItems || []}
            stats={swotData.stats}
            loading={loading}
            onRefresh={fetchSWOTAnalysis}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            instanceId={instanceId}
          />
        )}
      </MainContent>
    </Container>
  );
}

export default SWOTScreen;
